import {
  useAssignUserRole,
  useRemoveUserRole,
  useRoles,
  useUserRoles,
} from '@/domains/access-control';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import type { RoleSummary } from '@skewb-works/contracts';
import { Lock, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

/**
 * Manage a user's DB access-control roles. Options come from `useRoles`, current
 * assignments from `useUserRoles`. Selections are held as a local draft and only
 * persisted (as a delta of assigns/removes) when the user clicks Save; Cancel and
 * closing discard the draft.
 */
export function AssignRoleDialog({ open, onOpenChange, userId }: AssignRoleDialogProps) {
  const numericUserId = Number(userId);

  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: userRolesData, isLoading: userRolesLoading } = useUserRoles(numericUserId);
  const assign = useAssignUserRole();
  const remove = useRemoveUserRole();

  const roles: RoleSummary[] = rolesData?.data ?? [];
  const assignedRoleIds = useMemo(
    () => new Set<number>(userRolesData?.roleIds ?? []),
    [userRolesData],
  );

  // Surface the user's currently-assigned roles at the top of the list, just under the built-in
  // Admin role (the system role with the lock badge — note other "…Administrator" roles also have
  // isAdmin but aren't system roles), so it's obvious what they already have. Ordering keys off the
  // persisted assignments — not the live draft — so rows don't jump around as checkboxes are
  // toggled. Within each rank, roles are grouped by provider (Cadent before LDP, everything else
  // last) so the list reads as coherent sections instead of a mixed jumble.
  const orderedRoles = useMemo<RoleSummary[]>(() => {
    const rank = (role: RoleSummary) => {
      if (role.isAdmin && role.isSystem) return 0;
      if (assignedRoleIds.has(role.id)) return 1;
      return 2;
    };
    // Group by the provider prefix in the role name so Cadent roles cluster ahead of LDP ones.
    const groupRank = (role: RoleSummary) => {
      if (/^cadent\b/i.test(role.name)) return 0;
      if (/^ldp\b/i.test(role.name)) return 1;
      return 2;
    };
    return roles
      .map((role, index) => ({ role, index }))
      .sort(
        (a, b) =>
          rank(a.role) - rank(b.role) || groupRank(a.role) - groupRank(b.role) || a.index - b.index,
      )
      .map(({ role }) => role);
  }, [roles, assignedRoleIds]);

  // Draft of the selected role ids, seeded from the persisted assignments on open only.
  // Seeding on the false→true open transition (not on every assignedRoleIds change) means a
  // background useUserRoles refetch while the dialog is open won't clobber an in-progress draft;
  // Cancel/close still discard because the next open re-seeds.
  const [selected, setSelected] = useState<Set<number>>(assignedRoleIds);
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) setSelected(new Set<number>(assignedRoleIds));
    prevOpen.current = open;
  }, [open, assignedRoleIds]);

  const handleToggle = (roleId: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(roleId);
      } else {
        next.delete(roleId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const toAdd = [...selected].filter((id) => !assignedRoleIds.has(id));
    const toRemove = [...assignedRoleIds].filter((id) => !selected.has(id));
    await Promise.all([
      ...toAdd.map((roleId) => assign.mutateAsync({ userId: numericUserId, roleId })),
      ...toRemove.map((roleId) => remove.mutateAsync({ userId: numericUserId, roleId })),
    ]);
    onOpenChange(false);
  };

  const isSaving = assign.isPending || remove.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage Roles</DialogTitle>
          <DialogDescription>
            Assign or remove access-control roles for this user. Roles grant permissions across the
            app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm">Roles</Label>
          </div>

          <div className="space-y-1 rounded-md border border-border p-1">
            {(rolesLoading || userRolesLoading) && (
              <span className="text-xs text-muted-foreground">Loading…</span>
            )}
            {!rolesLoading && roles.length === 0 && (
              <span className="text-xs text-muted-foreground italic">No roles available.</span>
            )}
            {orderedRoles.map((role) => {
              const checked = selected.has(role.id);
              return (
                <label
                  key={role.id}
                  className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-accent/50 cursor-pointer"
                >
                  <Checkbox
                    aria-label={role.name}
                    checked={checked}
                    onCheckedChange={(value) => handleToggle(role.id, value === true)}
                  />
                  <div className="flex flex-col">
                    <span className="flex items-center gap-2 text-sm text-foreground">
                      {role.name}
                      {role.isSystem && (
                        <span
                          className="flex items-center gap-1 text-xs text-muted-foreground"
                          title="System role"
                        >
                          <Lock className="size-3" />
                          System
                        </span>
                      )}
                    </span>
                    {role.description && (
                      <span className="text-xs text-muted-foreground">{role.description}</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            className="text-white"
            style={{ backgroundColor: '#E84A2B' }}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
